import { useEffect, useMemo, useState } from "react";
import { At } from "@phosphor-icons/react";
import { filterMentionCandidates } from "@/lib/mentions";

/**
 * Composer mention suggestion popover. Anchored below the composer
 * (mobile-first — avoids the iOS keyboard obscuring it) with touch-safe
 * rows (48px tap target). Keyboard arrow-keys + Enter work on desktop;
 * tap works everywhere.
 *
 * Props:
 *   open     — show when a mention is being typed
 *   query    — the typed portion after `@` (without the `@`)
 *   candidates — [{ profile_id, full_name, email }]
 *   selfId   — exclude the current user
 *   onSelect(candidate)
 *   onClose()
 */
export default function MentionSuggest({
  open, query, candidates, selfId, onSelect, onClose,
}) {
  const filtered = useMemo(
    () => filterMentionCandidates(candidates, query, selfId),
    [candidates, query, selfId],
  );
  const [active, setActive] = useState(0);

  // Reset active index when the filter changes.
  useEffect(() => { setActive(0); }, [query, filtered.length]);

  // Arrow-key navigation — attaches only while open so other composers
  // (e.g., nested textareas) aren't affected.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (filtered.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => (i + 1) % filtered.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => (i - 1 + filtered.length) % filtered.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const pick = filtered[active];
        if (pick) onSelect(pick);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, filtered, active, onSelect, onClose]);

  if (!open || filtered.length === 0) return null;

  return (
    <div
      role="listbox"
      aria-label="Mention suggestions"
      data-testid="mention-suggest"
      className="absolute bottom-full left-0 right-0 z-40 mb-2 max-h-64 overflow-y-auto rounded-2xl border border-neutral-200 bg-white p-1.5 shadow-lg"
    >
      <p className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-500">
        <At size={10} weight="bold" className="mr-1 inline-block" />
        Mention someone
      </p>
      {filtered.map((c, idx) => {
        const initial = (c.full_name?.[0] || c.email?.[0] || "?").toUpperCase();
        const isActive = idx === active;
        return (
          <button
            key={c.profile_id}
            type="button"
            role="option"
            aria-selected={isActive}
            data-testid={`mention-suggest-option-${c.profile_id}`}
            data-active={isActive}
            onMouseEnter={() => setActive(idx)}
            // Use onMouseDown (not onClick) so focus never leaves the
            // textarea — onClick fires after blur and would close the
            // popover before we can insert.
            onMouseDown={(e) => { e.preventDefault(); onSelect(c); }}
            className={`flex min-h-[48px] w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm transition ${
              isActive ? "bg-primary/10" : "hover:bg-neutral-50"
            }`}
          >
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-white">
              {initial}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-neutral-900">
                {c.full_name || c.email}
              </span>
              {c.email && c.email !== c.full_name && (
                <span className="block truncate text-[11px] text-neutral-500">
                  {c.email}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
